// Stage 2D-2: party statement (read-only). For one person or company: the opening balance, every posted movement and the
// running balance on each of their party accounts: loans, advances, payables and receivables. Posted entries only.
import { useState, useEffect, useMemo } from "react";
import { Modal, Button, Notice, SkeletonTable } from "./kit/components";
import { T, text } from "./kit/tokens";
import { partyStatement } from "./api";
import { errText } from "./shared";
import { npr, DateChip, PeriodPicker, defaultPeriod } from "./workflow";

export default function PartyStatement({ party, ctx, onClose, onOpenEntry }) {
    const { data, cal, display } = ctx;
    const [period, setPeriod] = useState(() => defaultPeriod(cal, data.years));
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        if (!party) return;
        setRows(null); setErr("");
        partyStatement(party.id, period.from, period.to).then(setRows).catch(e => setErr(errText(e)));
    }, [party, period]);
    const groups = useMemo(() => {
        const g = new Map();
        for (const r of rows || []) { if (!g.has(r.account_id)) g.set(r.account_id, { code: r.code, name: r.name, lines: [] }); g.get(r.account_id).lines.push(r); }
        return [...g.values()];
    }, [rows]);
    if (!party) return null;
    return (
        <Modal open width={860} title={`Statement — ${party.name}`} onClose={onClose}
            subtitle="Loans, advances, payables and receivables of this person or company. Posted entries only."
            footer={<><span style={{ flex: 1 }} /><Button variant="secondary" onClick={onClose}>Close</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <PeriodPicker value={period} onChange={setPeriod} cal={cal} years={data.years} display={display} />
                {err && <Notice tone="danger">{err}</Notice>}
                {!rows && !err ? <SkeletonTable rows={4} cols={5} /> : groups.length === 0 ? (
                    <div style={{ ...text.caption, padding: 12 }}>Nothing posted for this person or company up to the end of the period.</div>
                ) : groups.map(g => {
                    const closing = g.lines[g.lines.length - 1]?.balance;
                    return (
                        <section key={g.code} style={{ border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: T.surface2, flexWrap: "wrap" }}>
                                <b style={{ color: T.textStrong }}>{g.code} {g.name}</b>
                                <span style={{ ...text.money, fontSize: 13 }}>Closing {npr(closing)}</span>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                    <thead><tr>{["Date", "Voucher", "Description", "Debit", "Credit", "Balance"].map((h, i) => <th key={h} style={{ textAlign: i >= 3 ? "right" : "left", color: T.textMuted, fontWeight: 600, fontSize: 11, padding: "6px 10px", borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                                    <tbody>{g.lines.map((l, i) => (
                                        <tr key={i} onClick={() => l.entry_id && onOpenEntry?.(l.entry_id)} style={{ cursor: l.entry_id ? "pointer" : "default", borderBottom: `1px solid ${T.borderSoft}` }}>
                                            <td style={{ padding: "6px 10px" }}><DateChip iso={l.line_date} cal={cal} display={display} /></td>
                                            <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>{l.display_no || ""}</td>
                                            <td style={{ padding: "6px 10px", color: l.is_opening ? T.textMuted : T.text }}>{l.narration}</td>
                                            <td style={{ padding: "6px 10px", textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.debit) ? npr(l.debit) : ""}</td>
                                            <td style={{ padding: "6px 10px", textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.credit) ? npr(l.credit) : ""}</td>
                                            <td style={{ padding: "6px 10px", textAlign: "right", ...text.money, fontSize: 13, fontWeight: 650 }}>{npr(l.balance)}</td>
                                        </tr>))}</tbody>
                                </table>
                            </div>
                        </section>
                    );
                })}
                <span style={text.caption}>Balances are shown on each account's normal side: for a loan or payable, what the company owes; for an advance or receivable, what is owed to the company.</span>
            </div>
        </Modal>
    );
}
